import { createSelector } from 'reselect';
import { Box3, Vector3 } from 'three';
import { create as createZustandStore } from 'zustand';

import { areSetsEqual } from '@paragon/client-tools';
import { Point3D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../../components/hooks';
import { LAYOUT_TO_DESIGN_COORDINATES_MATRIX } from '../../../library/layout-geometry/coordinates';
import { getAllTrussEnvelopeSolids } from '../../../store/selectors/layoutDocumentSelectors';
import { useCameraFrame } from '../useCameraFrame';

export function EnvelopeLodMonitor() {
  const update = useEnvelopeLodStore((state) => state.update);
  const envelopeSolidBounds = useAppSelector(getEnvelopeSolidBounds);

  useCameraFrame((frustum, cameraPosition) => {
    const hiddenEnvelopeGuids = new Set<string>();
    const simpleEnvelopeGuids = new Set<string>();

    for (const [envelopeGuid, bounds] of Object.entries(envelopeSolidBounds)) {
      if (!frustum.intersectsBox(bounds)) {
        hiddenEnvelopeGuids.add(envelopeGuid);
        continue;
      }

      const distance = bounds.distanceToPoint(cameraPosition);

      if (distance > HIDDEN_THRESHOLD) {
        hiddenEnvelopeGuids.add(envelopeGuid);
      } else if (distance > SIMPLE_THRESHOLD) {
        simpleEnvelopeGuids.add(envelopeGuid);
      }
    }

    update(Object.keys(envelopeSolidBounds).length, hiddenEnvelopeGuids, simpleEnvelopeGuids);
  });

  return null;
}

function box3FromParagonGeometryVertices(vertices: readonly Point3D[]): Box3 {
  const minX = Math.min(...vertices.map((v) => v.x));
  const minY = Math.min(...vertices.map((v) => v.y));
  const minZ = Math.min(...vertices.map((v) => v.z));

  const minVector = new Vector3(minX, minY, minZ);

  const maxX = Math.max(...vertices.map((v) => v.x));
  const maxY = Math.max(...vertices.map((v) => v.y));
  const maxZ = Math.max(...vertices.map((v) => v.z));
  const maxVector = new Vector3(maxX, maxY, maxZ);

  return new Box3(minVector, maxVector);
}

const getEnvelopeSolidBounds = createSelector(getAllTrussEnvelopeSolids, (solids) =>
  Object.fromEntries(
    Object.entries(solids)
      .map(([guid, solid]) =>
        solid
          ? ([
              guid,
              box3FromParagonGeometryVertices(solid.vertices).applyMatrix4(LAYOUT_TO_DESIGN_COORDINATES_MATRIX),
            ] as const)
          : null,
      )
      .filter(notNullish),
  ),
);

/** The distance threshold over which we want to avoid rendering plates. */
const HIDDEN_THRESHOLD = 600;

/** The distance threshold over which we want to render simplified plates (no holes). */
const SIMPLE_THRESHOLD = 200;

interface LodMonitorState {
  valid: boolean;
  totalEnvelopes: number;
  hiddenEnvelopeGuids: Set<string>;
  simpleEnvelopeGuids: Set<string>;

  update: (totalCount: number, hiddenEnvelopeGuids: Set<string>, simpleEnvelopeGuids: Set<string>) => void;
}

const useEnvelopeLodStore = createZustandStore<LodMonitorState>((set) => ({
  valid: false,

  totalEnvelopes: 0,

  hiddenEnvelopeGuids: new Set(),
  simpleEnvelopeGuids: new Set(),

  update(totalCount, hiddenEnvelopeGuids, simpleEnvelopeGuids) {
    set((state) => {
      const patch = { total: totalCount, valid: true } as Partial<LodMonitorState>;

      if (!areSetsEqual(hiddenEnvelopeGuids, state.hiddenEnvelopeGuids)) {
        patch.hiddenEnvelopeGuids = hiddenEnvelopeGuids;
      }

      if (!areSetsEqual(simpleEnvelopeGuids, state.simpleEnvelopeGuids)) {
        patch.simpleEnvelopeGuids = simpleEnvelopeGuids;
      }

      return patch;
    });
  },
}));

export function useSimplifiedEnvelopeGuids() {
  return useEnvelopeLodStore((state) => state.simpleEnvelopeGuids);
}

export function useHiddenEnvelopeGuids() {
  const result = useEnvelopeLodStore((state) => state.hiddenEnvelopeGuids);
  const valid = useEnvelopeLodStore((state) => state.valid);

  return valid ? result : 'invalid';
}
